import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button,
  Typography,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider
} from '@mui/material';
import { 
  Warning as WarningIcon,
  Report as ReportIcon,
  Security as SecurityIcon,
  LocationOn as LocationIcon,
  Fingerprint as FingerprintIcon
} from '@mui/icons-material';
import { useAssets } from '../../contexts/AssetsContext';
import { useAlerts } from '../../contexts/AlertsContext';
import { useAuth } from '../../contexts/AuthContext';
import FingerprintAuth from '../auth/FingerprintAuth';
import EmergencyAlertScreen from './EmergencyAlertScreen';

const PanicAlertDialog = ({ open, onClose }) => {
  const { assets, getUserAssets } = useAssets();
  const { addAlert } = useAlerts();
  const { currentUser } = useAuth();
  
  const [activeStep, setActiveStep] = useState(0);
  const [alertType, setAlertType] = useState('stolen');
  const [selectedAssetId, setSelectedAssetId] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [needsMedical, setNeedsMedical] = useState(null);
  const [medicalDetails, setMedicalDetails] = useState('');
  const [fingerprintDialogOpen, setFingerprintDialogOpen] = useState(false);
  const [emergencyScreenOpen, setEmergencyScreenOpen] = useState(false);
  const [createdAlert, setCreatedAlert] = useState(null);
  
  // Get user assets
  const userAssets = currentUser ? getUserAssets(currentUser.id) : [];
  
  const alertTypes = [
    { value: 'stolen', label: 'Asset Stolen', description: 'Asset has been stolen and immediate response is needed' },
    { value: 'missing', label: 'Asset Missing', description: 'Asset cannot be located but theft is not confirmed' },
    { value: 'emergency', label: 'Emergency Tracking', description: 'Emergency tracking and response is needed' },
    { value: 'tampered', label: 'Tampering Detected', description: 'Asset security may have been compromised' }
  ];
  
  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };
  
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };
  
  const handleReset = () => {
    setActiveStep(0);
    setAlertType('stolen');
    setSelectedAssetId('');
    setAdditionalInfo('');
    setNeedsMedical(null);
    setMedicalDetails('');
  };
  
  const handleSubmit = () => {
    setFingerprintDialogOpen(true);
  };
  
  const handleFingerprintSuccess = () => {
    setFingerprintDialogOpen(false);
    
    // Get the selected asset
    const selectedAsset = userAssets.find(asset => asset.id === selectedAssetId);
    
    if (selectedAsset) {
      // Create a new alert
      const newAlert = {
        id: `alert_${Date.now()}`,
        type: alertType.toUpperCase(),
        assetId: selectedAssetId,
        assetName: selectedAsset.name,
        userId: currentUser?.id || 'user_client1',
        timestamp: new Date().toISOString(),
        status: 'active',
        severity: alertType === 'stolen' ? 'critical' : 'warning',
        message: `${alertTypes.find(type => type.value === alertType)?.label}: ${selectedAsset.name}`,
        location: selectedAsset.location,
        additionalInfo: additionalInfo,
        needsMedical: needsMedical,
        medicalDetails: medicalDetails,
        acknowledged: false,
        assignedTo: null
      };
      
      // Add the alert
      addAlert(newAlert);
      
      // Store the created alert for the emergency screen
      setCreatedAlert(newAlert);
      
      // Open the emergency screen
      setEmergencyScreenOpen(true);
      
      // Move to the confirmation step
      setActiveStep(4);
    }
  };
  
  const handleClose = () => {
    if (activeStep === 3) {
      handleReset();
    }
    // Close the emergency screen if it's open
    if (emergencyScreenOpen) {
      setEmergencyScreenOpen(false);
    }
    onClose();
  };
  
  const handleEmergencyScreenClose = () => {
    console.log('Emergency screen closed');
    // Close the emergency screen and reset state
    setEmergencyScreenOpen(false);
    setCreatedAlert(null);
    
    // If we're in the confirmation step, close the entire dialog
    if (activeStep === 3) {
      handleReset();
      onClose();
    }
  };
  
  // Force close the emergency screen when the main dialog is closed
  useEffect(() => {
    if (!open && emergencyScreenOpen) {
      setEmergencyScreenOpen(false);
      setCreatedAlert(null);
    }
  }, [open, emergencyScreenOpen]);
  
  return (
    <>
      <Dialog 
        open={open} 
        onClose={activeStep === 4 ? handleClose : undefined}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, overflow: 'hidden' }
        }}
      >
        <DialogTitle sx={{ pb: 1, bgcolor: 'error.main', color: 'white' }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Emergency Alert
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <Stepper 
            activeStep={activeStep} 
            sx={{ 
              mt: '12px',
              mb: 4,
              '& .MuiStepLabel-root .Mui-completed': {
                color: 'error.main', // circle color (COMPLETED)
              },
              '& .MuiStepLabel-root .Mui-active': {
                color: 'error.main', // circle color (ACTIVE)
              },
            }}
            alternativeLabel
          >
            <Step>
              <StepLabel>Alert Type</StepLabel>
            </Step>
            <Step>
              <StepLabel>Asset Details</StepLabel>
            </Step>
            <Step>
              <StepLabel>Confirm</StepLabel>
            </Step>
            <Step>
              <StepLabel>Medical</StepLabel>
            </Step>
            <Step>
              <StepLabel>Submitted</StepLabel>
            </Step>
          </Stepper>
          
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                What type of emergency is this?
              </Typography>
              
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <RadioGroup
                  value={alertType}
                  onChange={(e) => setAlertType(e.target.value)}
                >
                  {alertTypes.map((type) => (
                    <Paper 
                      key={type.value}
                      elevation={alertType === type.value ? 3 : 1}
                      sx={{ 
                        mb: 2,
                        p: 2,
                        border: alertType === type.value ? '2px solid' : '1px solid',
                        borderColor: alertType === type.value ? 'error.main' : 'divider',
                        borderRadius: 2,
                        transition: 'all 0.2s'
                      }}
                    >
                      <FormControlLabel 
                        value={type.value} 
                        control={<Radio color="error" />} 
                        label={
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {type.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {type.description}
                            </Typography>
                          </Box>
                        }
                        sx={{ width: '100%', m: 0 }}
                      />
                    </Paper>
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
          )}
          
          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Which asset is affected?
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="asset-select-label">Select Asset</InputLabel>
                <Select
                  labelId="asset-select-label"
                  id="asset-select"
                  value={selectedAssetId}
                  label="Select Asset"
                  onChange={(e) => setSelectedAssetId(e.target.value)}
                  required
                >
                  {userAssets.map((asset) => (
                    <MenuItem key={asset.id} value={asset.id}>
                      {asset.name} ({asset.type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {selectedAssetId && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Selected Asset Details:
                  </Typography>
                  
                  {(() => {
                    const asset = userAssets.find(a => a.id === selectedAssetId);
                    if (!asset) return null;
                    
                    return (
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {asset.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {asset.description}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <LocationIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body2">
                            Last known location: Johannesburg, South Africa
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Value: R{asset.value.toLocaleString()}
                        </Typography>
                      </Paper>
                    );
                  })()}
                </Box>
              )}
              
              <TextField
                label="Additional Information"
                multiline
                rows={4}
                fullWidth
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
                placeholder="Please provide any additional details that might help (e.g., last seen location, circumstances, etc.)"
                sx={{ mt: 2 }}
              />
            </Box>
          )}
          
          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Confirm Emergency Alert
              </Typography>
              
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 3, 
                  mb: 3, 
                  borderRadius: 2,
                  bgcolor: 'error.50',
                  borderColor: 'error.200'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <ReportIcon sx={{ color: 'error.main', mr: 1.5, mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.main' }}>
                      {alertTypes.find(type => type.value === alertType)?.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {alertTypes.find(type => type.value === alertType)?.description}
                    </Typography>
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                {(() => {
                  const asset = userAssets.find(a => a.id === selectedAssetId);
                  if (!asset) return null;
                  
                  return (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Asset Information:
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {asset.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {asset.type} • R{asset.value.toLocaleString()}
                      </Typography>
                    </Box>
                  );
                })()}
                
                {additionalInfo && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Additional Information:
                    </Typography>
                    <Typography variant="body2">
                      {additionalInfo}
                    </Typography>
                  </Box>
                )}
              </Paper>
              
              <Box sx={{ bgcolor: 'warning.50', p: 2, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                  <SecurityIcon sx={{ mr: 1, color: 'warning.main' }} />
                  <span>
                    <strong>Important:</strong> Submitting this alert will notify security personnel and may trigger an emergency response. Fingerprint authentication is required to proceed.
                  </span>
                </Typography>
              </Box>
            </Box>
          )}
          
          {activeStep === 3 && (
            <Box sx={{ py: 3, px: 2 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'error.main' }}>
                Medical Information
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                  Do you or anyone with you require medical attention?
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Button 
                    variant={needsMedical === true ? "contained" : "outlined"}
                    color="error"
                    onClick={() => setNeedsMedical(true)}
                    sx={{ flex: 1, py: 1.5, borderRadius: 2 }}
                  >
                    Yes, Need Medical Help
                  </Button>
                  <Button 
                    variant={needsMedical === false ? "contained" : "outlined"}
                    color={needsMedical === false ? "success" : "primary"}
                    onClick={() => setNeedsMedical(false)}
                    sx={{ flex: 1, py: 1.5, borderRadius: 2 }}
                  >
                    No, We're Safe
                  </Button>
                </Box>
              </Box>
              
              {needsMedical === true && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 500 }}>
                    Please provide details about the medical situation:
                  </Typography>
                  
                  <TextField
                    multiline
                    rows={3}
                    fullWidth
                    placeholder="Describe any injuries, symptoms, or medical conditions that emergency responders should know about..."
                    value={medicalDetails}
                    onChange={(e) => setMedicalDetails(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  
                  <Typography variant="caption" color="text.secondary">
                    This information will be shared with emergency medical responders.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          
          {activeStep === 4 && (
            <Box sx={{ textAlign: 'center', py: 3, px: 2 }}>
              <Box 
                sx={{ 
                  width: 100, 
                  height: 100, 
                  borderRadius: '50%', 
                  bgcolor: 'success.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3
                }}
              >
                <SecurityIcon sx={{ fontSize: 60, color: 'success.main' }} />
              </Box>
              
              <Typography variant="h5" sx={{ color: 'success.main', fontWeight: 600, mb: 2 }}>
                Alert Submitted Successfully
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 4 }}>
                Your emergency alert has been submitted and security personnel have been notified.
                {needsMedical && " Medical services have also been requested."}
              </Typography>
              
              <Paper variant="outlined" sx={{ p: 3, maxWidth: 400, mx: 'auto', mb: 3, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Alert Reference Number:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {`ALT-${Date.now().toString().substring(6)}`}
                </Typography>
              </Paper>
              
              <Typography variant="body2" color="text.secondary">
                You can view and manage this alert in the Alerts section.
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, mt: 2 }}>
          {activeStep === 0 && (
            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
              <Button 
                onClick={handleClose} 
                color="inherit"
                sx={{ px: 3 }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleNext} 
                variant="contained" 
                color="error"
                disabled={!alertType}
                sx={{ px: 4, py: 1, borderRadius: 2 }}
              >
                Next
              </Button>
            </Box>
          )}
          
          {activeStep === 1 && (
            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
              <Button 
                onClick={handleBack} 
                color="inherit"
                sx={{ px: 3 }}
              >
                Back
              </Button>
              <Button 
                onClick={handleNext} 
                variant="contained" 
                color="error"
                disabled={!selectedAssetId}
                sx={{ px: 4, py: 1, borderRadius: 2 }}
              >
                Next
              </Button>
            </Box>
          )}
          
          {activeStep === 2 && (
            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
              <Button 
                onClick={handleBack} 
                color="inherit"
                sx={{ px: 3 }}
              >
                Back
              </Button>
              <Button 
                onClick={handleNext} 
                variant="contained" 
                color="error"
                sx={{ px: 4, py: 1, borderRadius: 2 }}
              >
                Next
              </Button>
            </Box>
          )}
          
          {activeStep === 3 && (
            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
              <Button 
                onClick={handleBack} 
                color="inherit"
                sx={{ px: 3 }}
              >
                Back
              </Button>
              <Button 
                onClick={handleSubmit} 
                variant="contained" 
                color="error"
                disabled={needsMedical === null}
                startIcon={<FingerprintIcon />}
                sx={{ px: 4, py: 1, borderRadius: 2 }}
              >
                Authenticate & Submit
              </Button>
            </Box>
          )}
          
          {activeStep === 4 && (
            <Button 
              onClick={handleClose} 
              variant="contained" 
              color="primary" 
              fullWidth
              sx={{ py: 1.5, borderRadius: 2 }}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      <FingerprintAuth 
        open={fingerprintDialogOpen}
        onSuccess={handleFingerprintSuccess}
        onCancel={() => setFingerprintDialogOpen(false)}
        title="Authenticate to Submit Alert"
      />
      
      {/* Emergency Alert Screen */}
      <EmergencyAlertScreen 
        open={emergencyScreenOpen}
        onClose={handleEmergencyScreenClose}
        alertDetails={createdAlert}
      />
    </>
  );
};

export default PanicAlertDialog;
